import type { MapaEstados } from './estadoPecas'
import type { AncoraOcupante, PerfilVeiculo } from './perfilVeiculo'
import { alturaSentado, type ProporcoesVitima } from './perfisVitima'

/**
 * Estados do teto.
 *
 * `PROMPT2.md` §21 proíbe deformação real da carroceria e dano procedural; §8
 * endossa peças pré-modeladas em estados alternativos, que é a estratégia já
 * usada na coluna B segmentada. Então o teto tem estados discretos, não
 * deformação: previsível, conferível por teste e suficiente para a pergunta que
 * o instrutor quer responder — quanto espaço cada técnica devolve.
 */
export type EstadoTeto = 'integro' | 'rebaixado' | 'elevado' | 'rebatido' | 'retirado'

export type ColunaEstrutural = 'colunaA' | 'colunaB' | 'colunaC'

export interface TecnicaDeTeto {
  id: EstadoTeto
  nome: string
  descricao: string
  /** variação da linha do teto, em metros; negativa comprime o habitáculo */
  deltaTetoY: number
  /** o teto ainda limita a altura útil do habitáculo? */
  limitaAltura: boolean
  /** o painel de teto continua no lugar */
  tetoPresente: boolean
  /** teto rebatido para trás, ainda presente mas fora do vão */
  tetoRebatido: boolean
  /** colunas cortadas e retiradas: somem da cena e da conta de obstáculos */
  colunasRemovidas: ColunaEstrutural[]
  /** colunas cortadas e estendidas: continuam presentes, e continuam atrapalhando */
  colunasSeccionadas: ColunaEstrutural[]
  /** é dano do acidente, não manobra da equipe */
  eDano: boolean
}

const TODAS: ColunaEstrutural[] = ['colunaA', 'colunaB', 'colunaC']

export const TECNICAS_DE_TETO: Record<EstadoTeto, TecnicaDeTeto> = {
  integro: {
    id: 'integro',
    nome: 'Íntegro',
    descricao: 'Veículo como chegou, sem dano no teto e sem intervenção.',
    deltaTetoY: 0,
    limitaAltura: true,
    tetoPresente: true,
    tetoRebatido: false,
    colunasRemovidas: [],
    colunasSeccionadas: [],
    eDano: false,
  },
  rebaixado: {
    id: 'rebaixado',
    nome: 'Amassado',
    descricao:
      'Teto rebaixado pelo impacto. Não é técnica: é o dano, e é o que reduz o espaço com que a equipe vai ter de trabalhar.',
    deltaTetoY: -0.22,
    limitaAltura: true,
    tetoPresente: true,
    tetoRebatido: false,
    colunasRemovidas: [],
    colunasSeccionadas: [],
    eDano: true,
  },
  elevado: {
    id: 'elevado',
    nome: 'Elevação do teto',
    descricao:
      'Colunas seccionadas e teto erguido. Abre altura sem retirar o teto — as colunas continuam no lugar e continuam no caminho da rota.',
    deltaTetoY: 0.2,
    limitaAltura: true,
    tetoPresente: true,
    tetoRebatido: false,
    colunasRemovidas: [],
    colunasSeccionadas: TODAS,
    eDano: false,
  },
  rebatido: {
    id: 'rebatido',
    nome: 'Rebatimento do teto',
    descricao:
      'Colunas A cortadas e teto rebatido para trás. O vão dianteiro deixa de ter limite de altura.',
    deltaTetoY: 0,
    limitaAltura: false,
    tetoPresente: true,
    tetoRebatido: true,
    colunasRemovidas: ['colunaA'],
    colunasSeccionadas: [],
    eDano: false,
  },
  retirado: {
    id: 'retirado',
    nome: 'Retirada total do teto',
    descricao:
      'Todas as colunas cortadas e teto removido. É a intervenção que mais abre espaço — e a que mais custa em tempo e ferramenta.',
    deltaTetoY: 0,
    limitaAltura: false,
    tetoPresente: false,
    tetoRebatido: false,
    colunasRemovidas: TODAS,
    colunasSeccionadas: [],
    eDano: false,
  },
}

export const ESTADOS_TETO = Object.keys(TECNICAS_DE_TETO) as EstadoTeto[]

export const tecnicaDeTeto = (estado: EstadoTeto): TecnicaDeTeto => TECNICAS_DE_TETO[estado]

/**
 * Perfil com a linha do teto alterada.
 *
 * `tetoY` é a fonte da altura das colunas, da silhueta lateral, do topo dos
 * vidros, dos airbags de cortina e da faixa de altura dos obstáculos. Mudar o
 * teto aqui faz todo o resto acompanhar, sem tocar em cada lugar.
 */
export function perfilComTeto(perfil: PerfilVeiculo, estado: EstadoTeto): PerfilVeiculo {
  const tecnica = tecnicaDeTeto(estado)
  if (tecnica.deltaTetoY === 0) return perfil
  return {
    ...perfil,
    tetoY: perfil.tetoY + tecnica.deltaTetoY,
    altura: perfil.altura + tecnica.deltaTetoY,
  }
}

const LADOS = ['Esquerda', 'Direita'] as const

/**
 * Estado das peças que a técnica altera.
 *
 * Devolve um patch de `MapaEstados` em vez de um mecanismo próprio: o cálculo
 * de obstáculos e a cena já reagem a estado de peça, então uma coluna cortada
 * sai da lista e some da tela sem nenhuma ligação nova.
 */
export function estadosDaTecnica(estado: EstadoTeto): MapaEstados {
  const tecnica = tecnicaDeTeto(estado)
  const mapa: Record<string, 'removida' | 'aberta'> = {}

  for (const coluna of tecnica.colunasRemovidas) {
    for (const lado of LADOS) mapa[`${coluna}${lado}`] = 'removida'
  }
  if (!tecnica.tetoPresente) {
    mapa.teto = 'removida'
    mapa.reforcosTeto = 'removida'
  } else if (tecnica.tetoRebatido) {
    mapa.teto = 'aberta'
  }
  return mapa
}

/** Folga entre o topo da cabeça do ocupante sentado e a linha do teto. */
export function folgaDeCabeca(
  perfil: PerfilVeiculo,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
): number {
  return perfil.tetoY - (ancora.posicao.y + alturaSentado(proporcoes))
}

/**
 * Folga de cabeça no estado de teto dado.
 *
 * `null` significa "sem teto acima": rebatido ou retirado deixam de limitar a
 * altura, e informar um número ali seria inventar um limite que não existe.
 */
export function folgaDeCabecaComTeto(
  perfilBase: PerfilVeiculo,
  estado: EstadoTeto,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
): number | null {
  if (!tecnicaDeTeto(estado).limitaAltura) return null
  return folgaDeCabeca(perfilComTeto(perfilBase, estado), ancora, proporcoes)
}

/**
 * Quanto a técnica muda o espaço em relação ao veículo íntegro.
 * `null` quando o teto sai de cena e o ganho deixa de ser mensurável.
 */
export function ganhoDeEspaco(
  perfilBase: PerfilVeiculo,
  estado: EstadoTeto,
  ancora: AncoraOcupante,
  proporcoes: ProporcoesVitima,
): number | null {
  const comTeto = folgaDeCabecaComTeto(perfilBase, estado, ancora, proporcoes)
  if (comTeto === null) return null
  return comTeto - folgaDeCabeca(perfilBase, ancora, proporcoes)
}
